import { useState, useEffect, useCallback } from 'react';
import { fetchWellEligibleItems, throwItemsIntoWell } from '../../services/api';

/**
 * Custom hook for managing Well dialog data and operations
 * Separates data fetching logic from UI components
 * 
 * @param {boolean} open - Whether the dialog is open
 * @param {string} token - Auth token
 * @returns {Object} Well data and operations
 */
export const useWellData = (open, token) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [eligibleItems, setEligibleItems] = useState(null);
    const [filters, setFilters] = useState(null);
    
    // Transform server items to UI format
    const transformItems = useCallback((serverItems) => {
        return serverItems.map(item => ({
            id: item.id,
            name: item.name,
            icon: item.icon,
            quantity: item.quantity,
            weight: item.weight || 0,
            category: item.category,
            maxThrowQuantity: item.maxThrowQuantity
        }));
    }, []);
    
    // Load eligible items from server
    const loadEligibleItems = useCallback(async () => {
        if (!token) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const data = await fetchWellEligibleItems(token);
            const transformedItems = transformItems(data.items || []);
            
            setEligibleItems(transformedItems);
            setFilters(data.filters || null);
            
            if (transformedItems.length === 0) {
                setError('No items available to throw into the well');
            }
        } catch (err) {
            console.error('Error fetching eligible items:', err);
            setError(err.message || 'Failed to load eligible items');
        } finally {
            setLoading(false);
        }
    }, [token, transformItems]);
    
    // Throw items into the well
    const throwItems = useCallback(async (selectedItems) => {
        if (!selectedItems || selectedItems.length === 0) {
            throw new Error('No items selected');
        }
        
        // Transform and validate items
        const itemsToThrow = selectedItems
            .map(item => ({
                itemId: String(item.itemId),
                quantity: Number(item.quantity)
            }))
            .filter(item => item.itemId && item.itemId !== '0' && item.quantity > 0);
        
        if (itemsToThrow.length === 0) {
            throw new Error('Invalid item selection');
        }
        
        const response = await throwItemsIntoWell(token, { items: itemsToThrow });
        
        if (!response.success) {
            throw new Error(response.message || 'Failed to throw items into well');
        }
        
        return response;
    }, [token]);
    
    // Load items when dialog opens
    useEffect(() => {
        if (open && token) {
            loadEligibleItems();
        }
    }, [open, token, loadEligibleItems]);
    
    return {
        loading,
        error,
        eligibleItems,
        filters,
        throwItems,
        reload: loadEligibleItems
    };
};

export default useWellData;